// react
import { Dispatch, FormEvent, SetStateAction, useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	Account,
	Benefit,
	Contract,
	Entity,
	EntityLoan,
	ExportResource,
	ExpenseType,
	Transaction,
	Wallet,
	WithdrawalApplication,
} from '../../../@types';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { RadioBoxes } from '../../fields/RadioBoxes/RadioBoxes';

// utils
import { formatDate } from '../../../utils/dates';
import { downloadFile, jsonToCsv } from '../../../utils/files';
import {
	translateStatus,
	translateTransactionType,
	translateEntityType,
} from '../../../utils/translations';
import { ContextBanner } from '../../../App.context';

// props
export interface FormExportProps {
	onClose?: () => void;
	resource: ExportResource;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormExport = ({ onClose, resource, setIsLoading }: FormExportProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const { idBeneficiary, idEntity, idSupplier } = useParams();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [searchParams] = useSearchParams();

	// params
	const paramContract = searchParams.get('contract');
	const paramExpenseType = searchParams.get('expenseType');
	const paramFromAccount = searchParams.get('fromAccount');
	const paramLoan = searchParams.get('loan');
	const paramStatus = searchParams.get('status');
	const paramSubType = searchParams.get('subtype');
	const paramSupplier = searchParams.get('supplier');
	const paramToAccount = searchParams.get('toAccount');
	const paramToEntity = searchParams.get('toEntity');
	const paramWallet = searchParams.get('wallet');

	// vars
	const dateToday = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
	const headers = {
		Authorization: `Bearer ${accessToken}`,
		User: String(idToken),
	};
	const idForm = `form-export`;
	const isTransactions = (
		['CASH_BENEFIT', 'NON_CASH_BENEFIT', 'OPEX', 'PAYMENT', 'TRANSFER'] as ExportResource[]
	).includes(resource);

	const optionsRadioBoxes = [
		{
			description: {
				children: t('allData.description'),
			},
			id: `${idForm}-scope-all`,
			label: {
				children: t('allData.label'),
			},
			value: 'ALL',
		},
		{
			description: {
				children: t('filteredData.description'),
			},
			id: `${idForm}-scope-filtered`,
			label: {
				children: t('filteredData.label'),
			},
			value: 'FILTERED',
		},
	];

	// fetch CASH_BENEFIT transactions
	const { fetchRequest: fetchCashBenefit } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('date')]: null,
					[t('fromEntity')]: null,
					[t('fromAccount')]: null,
					[t('toEntity')]: null,
					[t('amount')]: null,
					[t('currency')]: null,
					[t('expenseType')]: null,
					[t('withdrawalApplication')]: null,
					[t('status')]: null,
					[t('transactionsEvidenced')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((transaction: Transaction) => {
					const expenseType = transaction.expenseType as ExpenseType;
					const fromAccount = transaction.fromAccount as Account;
					const fromEntity = transaction.fromEntity as Entity;
					const toEntity = transaction.toEntity as Entity;
					const wa = transaction.withdrawalApplication as WithdrawalApplication;

					return {
						[t('id')]: transaction.id,
						[t('type')]: translateTransactionType(transaction.type, t),
						[t('date')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
						[t('fromEntity')]: fromEntity?.name,
						[t('fromAccount')]: fromAccount?.name,
						[t('toEntity')]: toEntity?.name,
						[t('amount')]: transaction.amount,
						[t('currency')]: transaction.currency,
						[t('expenseType')]: expenseType?.name,
						[t('withdrawalApplication')]: wa?.idWbg,
						[t('status')]: translateStatus(transaction.status, t),
						[t('transactionsEvidenced')]: transaction.traceability.value,
						[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.cashBenefits', {
					date: dateToday,
					project: project?.idWbg,
				}),
				type: 'text/csv',
			});
		},
	});

	// fetch OPEX transactions
	const { fetchRequest: fetchOpEx } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('date')]: null,
					[t('fromEntity')]: null,
					[t('fromAccount')]: null,
					[t('amount')]: null,
					[t('currency')]: null,
					[t('expenseType')]: null,
					[t('withdrawalApplication')]: null,
					[t('status')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((transaction: Transaction) => {
					const expenseType = transaction.expenseType as ExpenseType;
					const fromAccount = transaction.fromAccount as Account;
					const fromEntity = transaction.fromEntity as Entity;
					const wa = transaction.withdrawalApplication as WithdrawalApplication;

					return {
						[t('id')]: transaction.id,
						[t('type')]: translateTransactionType(transaction.type, t),
						[t('date')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
						[t('fromEntity')]: fromEntity?.name,
						[t('fromAccount')]: fromAccount?.name,
						[t('amount')]: transaction.amount,
						[t('currency')]: transaction.currency,
						[t('expenseType')]: expenseType?.name,
						[t('withdrawalApplication')]: wa?.idWbg,
						[t('status')]: translateStatus(transaction.status, t),
						[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}
			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.opex', { date: dateToday, project: project?.idWbg }),
				type: 'text/csv',
			});
		},
	});

	// fetch PAYMENT transactions
	const { fetchRequest: fetchPayments } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('date')]: null,
					[t('fromEntity')]: null,
					[t('fromAccount')]: null,
					[t('toEntity')]: null,
					[t('amount')]: null,
					[t('currency')]: null,
					[t('expenseType')]: null,
					[t('withdrawalApplication')]: null,
					[t('status')]: null,
					[t('transactionsEvidenced')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((transaction: Transaction) => {
					const expenseType = transaction.expenseType as ExpenseType;
					const fromAccount = transaction.fromAccount as Account;
					const fromEntity = transaction.fromEntity as Entity;
					const toEntity = transaction.toEntity as Entity;
					const wa = transaction.withdrawalApplication as WithdrawalApplication;

					return {
						[t('id')]: transaction.id,
						[t('type')]: translateTransactionType(transaction.type, t),
						[t('date')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
						[t('fromEntity')]: fromEntity?.name,
						[t('fromAccount')]: fromAccount?.name,
						[t('toEntity')]: toEntity?.name,
						[t('amount')]: transaction.amount,
						[t('currency')]: transaction.currency,
						[t('expenseType')]: expenseType?.name,
						[t('withdrawalApplication')]: wa?.idWbg,
						[t('status')]: translateStatus(transaction.status, t),
						[t('transactionsEvidenced')]: transaction.traceability.value,
						[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.payments', { date: dateToday, project: project?.idWbg }),
				type: 'text/csv',
			});
		},
	});

	// fetch TRANSFER transactions
	const { fetchRequest: fetchTransfers } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('date')]: null,
					[t('fromEntity')]: null,
					[t('fromAccount')]: null,
					[t('amount')]: null,
					[t('currency')]: null,
					[t('toEntity')]: null,
					[t('toAccount')]: null,
					[t('amountReceived')]: null,
					[t('currencyReceived')]: null,
					[t('status')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((transaction: Transaction) => {
					const fromAccount = transaction.fromAccount as Account;
					const fromEntity = transaction.fromEntity as Entity;
					const toAccount = transaction.toAccount as Account;
					const toEntity = transaction.toEntity as Entity;

					return {
						[t('id')]: transaction.id,
						[t('type')]: translateTransactionType(transaction.type, t),
						[t('date')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
						[t('fromEntity')]: fromEntity?.name,
						[t('fromAccount')]: fromAccount?.name,
						[t('amount')]: transaction.amount,
						[t('currency')]: transaction.currency,
						[t('toEntity')]: toEntity?.name,
						[t('toAccount')]: toAccount?.name,
						[t('amountReceived')]: transaction.amountReceived,
						[t('currencyReceived')]: transaction.currencyReceived,
						[t('status')]: translateStatus(transaction.status, t),
						[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.transfers', {
					date: dateToday,
					project: project?.idWbg,
				}),
				type: 'text/csv',
			});
		},
	});

	// fetch CONTRACTs
	const { fetchRequest: fetchContracts } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			// Collect all unique currencies across all contracts
			const allCurrencies = new Set<string>();
			if (res.data) {
				res.data.forEach((contract: Contract) => {
					contract.amounts.forEach((amount) => {
						allCurrencies.add(amount.currency);
					});
				});
			}

			// Convert to sorted array for consistent column order
			const sortedCurrencies = Array.from(allCurrencies).sort();

			// Create header row with currency columns
			const baseHeaders = {
				[t('id')]: null,
				[t('supplier')]: null,
				[t('stepId')]: null,
				[t('description')]: null,
				[t('signingDate')]: null,
				[t('created')]: null,
				[t('modified')]: null,
			};

			// Add currency columns
			const currencyHeaders: Record<string, null> = {};
			sortedCurrencies.forEach((currency) => {
				currencyHeaders[currency] = null;
			});

			let data = [{ ...baseHeaders, ...currencyHeaders }];

			if (res.data && res.data.length) {
				data = res.data.map((contract: Contract) => {
					const baseData = {
						[t('id')]: contract.id,
						[t('entities')]: (contract.entities as Entity[])
							.map((entity) => entity.name)
							.join(', '),
						[t('stepId')]: contract.idStep,
						[t('description')]: contract.description,
						[t('signingDate')]: formatDate({ date: contract.dateSigning, format: 'YYYY-MM-DD' }),
						[t('created')]: formatDate({ date: contract.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: contract.modified, format: 'YYYY-MM-DD' }),
					};

					// Add currency amounts
					const currencyData: Record<string, number | null> = {};
					sortedCurrencies.forEach((currency) => {
						const amount = contract.amounts.find((amt) => amt.currency === currency);
						currencyData[currency] = amount ? amount.amount : null;
					});

					return { ...baseData, ...currencyData };
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.contracts', { date: dateToday, project: project?.idWbg }),
				type: 'text/csv',
			});
		},
	});

	// fetch NON_CASH_BENEFITS transactions
	const { fetchRequest: fetchNonCashBenefits } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('date')]: null,
					[t('entity')]: null,
					[t('beneficiary')]: null,
					[t('quantity')]: null,
					[t('benefit')]: null,
					[t('status')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((transaction: Transaction) => {
					const fromEntity = transaction.fromEntity as Entity;
					const toEntity = transaction.toEntity as Entity;
					const benefit = transaction.benefit as Benefit;
					return {
						[t('id')]: transaction.id,
						[t('type')]: translateTransactionType(transaction.type, t),
						[t('date')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
						[t('entity')]: fromEntity?.name,
						[t('beneficiary')]: toEntity?.name,
						[t('quantity')]: transaction.quantity,
						[t('benefit')]: benefit?.name,
						[t('status')]: translateStatus(transaction.status, t),
						[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.nonCashBenefits', {
					date: dateToday,
					project: project?.idWbg,
				}),
				type: 'text/csv',
			});
		},
	});

	// fetch SUPPLIER detail
	const { fetchRequest: fetchSuppliers } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('name')]: null,
					[t('wallet')]: null,
					[t('transactionsEvidenced')]: null,
					[t('financialValueEvidenced')]: null,
					[t('entity')]: null,
					[t('status')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((entity: Entity) => {
					const wallet = entity.wallet as Wallet;
					const loans = entity.loans as EntityLoan[];
					return {
						[t('id')]: entity.id,
						[t('name')]: entity.name,
						[t('wallet')]: wallet?.address || t('na'),
						[t('transactionsEvidenced')]: entity.traceability.documentation.value,
						[t('financialValueEvidenced')]: entity.traceability.financial.value,
						[t('entity')]: (loans?.[0]?.parent as Entity)?.name,
						[t('status')]: translateStatus(wallet?.status || t('active'), t),
						[t('created')]: formatDate({ date: entity.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: entity.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.suppliers', {
					date: dateToday,
					project: project?.idWbg,
				}),
				type: 'text/csv',
			});
		},
	});

	// fetch BENEFICIARY detail
	const { fetchRequest: fetchBeneficiaries } = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			let data = [
				{
					[t('id')]: null,
					[t('type')]: null,
					[t('name')]: null,
					[t('wallet')]: null,
					[t('transactionsEvidenced')]: null,
					[t('financialValueEvidenced')]: null,
					[t('entity')]: null,
					[t('status')]: null,
					[t('created')]: null,
					[t('modified')]: null,
				},
			];

			if (res.data && res.data.length) {
				data = res.data.map((entity: Entity) => {
					const wallet = entity.wallet as Wallet;
					const loans = entity.loans as EntityLoan[];
					return {
						[t('id')]: entity.id,
						[t('type')]: translateEntityType(entity.subtype, t),
						[t('name')]: entity.name,
						[t('wallet')]: wallet?.address || t('na'),
						[t('transactionsEvidenced')]: entity.traceability.documentation.value,
						[t('financialValueEvidenced')]: entity.traceability.financial.value,
						[t('entity')]: (loans?.[0]?.parent as Entity)?.name,
						[t('status')]: translateStatus(wallet?.status || t('active'), t),
						[t('created')]: formatDate({ date: entity.created, format: 'YYYY-MM-DD' }),
						[t('modified')]: formatDate({ date: entity.modified, format: 'YYYY-MM-DD' }),
					};
				});
			}

			const csv = jsonToCsv({ array: data });

			downloadFile({
				data: csv,
				name: t('fileName.export.beneficiaries', {
					date: dateToday,
					project: project?.idWbg,
				}),
				type: 'text/csv',
			});
		},
	});

	const handleExport = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// set loading
		setIsLoading(true);

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const params = new URLSearchParams();

		let resFetch;

		if (project) params.set('project', String(project.id));
		if (entries.dateStart) params.set('dateStart', String(entries.dateStart));
		if (entries.dateEnd) params.set('dateEnd', String(entries.dateEnd));

		// add search params if scope is FILTERED
		if (entries.scope === 'FILTERED') {
			if (paramContract) params.set('contract', String(paramContract));
			if (paramExpenseType) params.set('expenseType', String(paramExpenseType));
			if (paramFromAccount) params.set('fromAccount', String(paramFromAccount));
			if (paramLoan) params.set('loan', String(paramLoan));
			if (paramStatus) params.set('status', String(paramStatus));
			if (paramSupplier) params.set('supplier', String(paramSupplier));
			if (paramToAccount) params.set('toAccount', String(paramToAccount));
			if (paramToEntity) params.set('toEntity', String(paramToEntity));
			if (paramWallet) params.set('wallet', String(paramWallet));
		}
		if (isTransactions) {
			const urlTransactions = `${process.env.REACT_APP_API_URL}/transactions`;
			params.set('type', resource);

			if (idBeneficiary) {
				params.set('toEntity', String(idBeneficiary));
			}

			if (idEntity) {
				params.set('fromEntity', String(idEntity));
			}

			if (idSupplier) {
				params.set('toEntity', String(idSupplier));
			}

			if (['CASH_BENEFIT', 'PAYMENT'].includes(resource)) {
				params.set(
					'populate',
					'contract,expenseType,fromAccount,fromEntity,toEntity,withdrawalApplication'
				);
				if (resource === 'CASH_BENEFIT') {
					resFetch = await fetchCashBenefit(`${urlTransactions}?${params}`, {
						headers,
					});
				} else if (resource === 'PAYMENT') {
					resFetch = await fetchPayments(`${urlTransactions}?${params}`, {
						headers,
					});
				}
			} else if (resource === 'OPEX') {
				params.set('populate', 'expenseType,fromAccount,fromEntity,withdrawalApplication');
				resFetch = await fetchOpEx(`${urlTransactions}?${params}`, {
					headers,
				});
			} else if (resource === 'TRANSFER') {
				params.set('populate', 'fromAccount,fromEntity,toAccount,toEntity');
				resFetch = await fetchTransfers(`${urlTransactions}?${params}`, {
					headers,
				});
			} else if (resource === 'NON_CASH_BENEFIT') {
				params.set('populate', 'fromEntity,toEntity,benefit');
				resFetch = await fetchNonCashBenefits(`${urlTransactions}?${params}`, {
					headers,
				});
			}
		} else if (resource === 'CONTRACT') {
			const urlContracts = `${process.env.REACT_APP_API_URL}/contracts`;
			params.set('populate', 'entities');
			resFetch = await fetchContracts(`${urlContracts}?${params}`, {
				headers,
			});
		} else if (resource === 'SUPPLIER') {
			const urlSuppliers = `${process.env.REACT_APP_API_URL}/entities`;
			params.set('populate', 'wallet,loans.parent');
			params.set('type', 'SUPPLIER');
			if (idEntity) params.set('parent', String(idEntity));
			resFetch = await fetchSuppliers(`${urlSuppliers}?${params}`, {
				headers,
			});
		} else if (resource === 'BENEFICIARY') {
			const urlBeneficiaries = `${process.env.REACT_APP_API_URL}/entities`;
			params.set('populate', 'wallet,loans.parent');
			params.set('type', 'BENEFICIARY');
			if (idEntity) params.set('parent', String(idEntity));
			if (paramSubType) params.set('subtype', String(paramSubType));
			resFetch = await fetchBeneficiaries(`${urlBeneficiaries}?${params}`, {
				headers,
			});
		}

		// clear loading
		setIsLoading(false);

		let message = t('alert.exportCreated');

		if (resFetch?.pagination?.total) {
			message = t('alert.exportCreatedRows', { rows: resFetch?.pagination?.total });
		}

		// set banner
		setBanner({
			hasClose: true,
			message,
			severity: 'success',
		});

		// close dialog
		if (onClose) {
			onClose();
		}
	};

	return (
		<form id={idForm} onSubmit={handleExport}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<RadioBoxes options={optionsRadioBoxes} />
				</Grid>
				{isTransactions && (
					<>
						<Grid item xs={12} md={6}>
							<Input
								inputProps={{
									max: new Date().toISOString().split('T')[0],
								}}
								label={t('startDate')}
								name="dateStart"
								type="date"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Input
								inputProps={{
									max: new Date().toISOString().split('T')[0],
								}}
								label={t('endDate')}
								name="dateEnd"
								type="date"
							/>
						</Grid>
					</>
				)}
			</Grid>
		</form>
	);
};
